import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { TokenManagerService } from './token-manager.service';
import {
  SystemNotificationCreatedEvent,
  SystemNotificationCenterUpdatedEvent,
  SystemNotificationItem,
  SystemNotificationPage,
  SystemNotificationUnreadCountResponse,
} from '../../shared/models/system-notification-center';

@Injectable({
  providedIn: 'root',
})
export class SystemNotificationCenterService {
  private readonly pageSize = 20;

  private readonly isOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly notificationsSubject = new BehaviorSubject<SystemNotificationItem[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly createdNotificationSubject = new BehaviorSubject<SystemNotificationCreatedEvent | null>(null);

  private currentPage = 0;
  private totalCount = 0;
  private initialized = false;
  private hubConnection: signalR.HubConnection | null = null;

  readonly isOpen$ = this.isOpenSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly createdNotification$ = this.createdNotificationSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly tokenManager: TokenManagerService,
  ) {}

  get isOpen(): boolean {
    return this.isOpenSubject.value;
  }

  get unreadCount(): number {
    return this.unreadCountSubject.value;
  }

  get notifications(): SystemNotificationItem[] {
    return this.notificationsSubject.value;
  }

  get hasMorePages(): boolean {
    if (this.currentPage === 0) {
      return true;
    }

    return this.notificationsSubject.value.length < this.totalCount;
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.loadUnreadCount();
    this.connectRealtime();
  }

  openPanel(): void {
    this.isOpenSubject.next(true);
    this.loadFirstPage();
  }

  closePanel(): void {
    this.isOpenSubject.next(false);
  }

  togglePanel(): void {
    if (this.isOpenSubject.value) {
      this.closePanel();
      return;
    }

    this.openPanel();
  }

  loadFirstPage(): void {
    this.currentPage = 0;
    this.totalCount = 0;
    this.notificationsSubject.next([]);
    this.loadNextPage();
  }

  loadNextPage(): void {
    if (this.loadingSubject.value || !this.hasMorePages) {
      return;
    }

    this.loadingSubject.next(true);
    const nextPage = this.currentPage + 1;
    const params = new HttpParams()
      .set('page', String(nextPage))
      .set('pageSize', String(this.pageSize));

    this.http
      .get<SystemNotificationPage>(`${environment.apiUrl}/api/system-notification`, { params })
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe({
        next: (response) => {
          this.currentPage = response.page;
          this.totalCount = response.totalCount;

          const currentItems = this.notificationsSubject.value;
          const merged = [...currentItems, ...response.items]
            .filter((item, index, array) => array.findIndex((candidate) => candidate.id === item.id) === index)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          this.notificationsSubject.next(merged);
        },
        error: () => {
          this.loadingSubject.next(false);
        },
      });
  }

  loadUnreadCount(): void {
    this.http
      .get<SystemNotificationUnreadCountResponse>(`${environment.apiUrl}/api/system-notification/unread-count`)
      .subscribe({
        next: ({ count }) => this.unreadCountSubject.next(Math.max(0, count)),
      });
  }

  markAsRead(notificationId: number): void {
    const target = this.notificationsSubject.value.find((item) => item.id === notificationId);
    if (!target || target.isRead) {
      return;
    }

    this.applyReadState([notificationId]);

    this.http.post<void>(`${environment.apiUrl}/api/system-notification/${notificationId}/read`, {}).subscribe({
      error: () => {
        this.loadUnreadCount();
      },
    });
  }

  markManyAsRead(notificationIds: number[]): void {
    const ids = notificationIds.filter((id, index, array) => array.indexOf(id) === index);
    if (ids.length === 0) {
      return;
    }

    const unreadIds = ids.filter((id) => {
      const notification = this.notificationsSubject.value.find((item) => item.id === id);
      return !!notification && !notification.isRead;
    });

    if (unreadIds.length === 0) {
      return;
    }

    this.applyReadState(unreadIds);

    this.http
      .post<void>(`${environment.apiUrl}/api/system-notification/read`, {
        notificationIds: unreadIds,
      })
      .subscribe({
        error: () => {
          this.loadUnreadCount();
        },
      });
  }

  markAllAsRead(): void {
    const unreadIds = this.notificationsSubject.value
      .filter((item) => !item.isRead)
      .map((item) => item.id);

    if (unreadIds.length === 0 && this.unreadCountSubject.value === 0) {
      return;
    }

    if (unreadIds.length > 0) {
      this.applyReadState(unreadIds);
    } else {
      this.unreadCountSubject.next(0);
    }

    this.http
      .post<void>(`${environment.apiUrl}/api/system-notification/read`, {
        notificationIds: [],
      })
      .subscribe({
        error: () => {
          this.loadUnreadCount();
          if (this.isOpenSubject.value) {
            this.loadFirstPage();
          }
        },
      });
  }

  private applyReadState(ids: number[]): void {
    let decremented = 0;
    const updated = this.notificationsSubject.value.map((item) => {
      if (!ids.includes(item.id) || item.isRead) {
        return item;
      }

      decremented += 1;
      return {
        ...item,
        isRead: true,
      };
    });

    this.notificationsSubject.next(updated);
    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - decremented));
  }

  private connectRealtime(): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.baseUrl}/ws/panel-notifications`, {
        withCredentials: false,
        accessTokenFactory: () => this.tokenManager.getToken() ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on('systemNotificationCenterUpdated', (payload: SystemNotificationCenterUpdatedEvent) => {
      const unreadCount = Number(payload?.unreadCount ?? 0);
      this.unreadCountSubject.next(Math.max(0, unreadCount));

      if (this.isOpenSubject.value) {
        this.loadFirstPage();
      }
    });

    this.hubConnection.on('systemNotificationCreated', (payload: SystemNotificationCreatedEvent) => {
      this.createdNotificationSubject.next(payload);
    });

    this.hubConnection.start().catch(() => undefined);
  }
}
